/**
 * Человеческий текст тарифов из правил (`PLAN_RULES`): списки «что входит»
 * и фразы лимитов собираются из тех же чисел, что проверяет сервер, —
 * экран тарифов не может разойтись с лимитами.
 */

import type { TFunction } from 'i18next';

import { PLAN_RULES, type Limit, type LimitWindow, type Metric, type Plan } from '@aptile/shared';

const METRIC_ORDER: readonly Metric[] = ['LESSONS', 'CHAT_MESSAGES', 'CALL_MINUTES', 'WORD_GAMES'];

/**
 * Ключ фразы лимита: метрика × окно. Сочетания, которых нет в правилах
 * тарифов, берут ближайшее (окно метрики по умолчанию).
 */
function limitKey(metric: Metric, window: LimitWindow) {
  switch (metric) {
    case 'LESSONS':
      return 'plans.limit.LESSONS.day' as const;
    case 'CHAT_MESSAGES':
      return window === 'week' ? ('plans.limit.CHAT_MESSAGES.week' as const) : ('plans.limit.CHAT_MESSAGES.month' as const);
    case 'CALL_MINUTES':
      return window === 'lifetime' ? ('plans.limit.trialCall' as const) : ('plans.limit.CALL_MINUTES.month' as const);
    case 'WORD_GAMES':
      return 'plans.limit.WORD_GAMES.day' as const;
  }
}

/** «150 сообщений в месяц», «1 урок в день», «пробный звонок до 3 минут». */
export function limitText(t: TFunction, metric: Metric, limit: Limit): string {
  return t(limitKey(metric, limit.window), { count: limit.amount });
}

/** Ключ фразы «ты использовал …» в модалке лимита. */
export function usedUpKey(metric: Metric | 'LEVEL', window: LimitWindow) {
  switch (metric) {
    case 'LEVEL':
      return 'paywall.level' as const;
    case 'LESSONS':
      return 'paywall.used.LESSONS.day' as const;
    case 'CHAT_MESSAGES':
      return window === 'week' ? ('paywall.used.CHAT_MESSAGES.week' as const) : ('paywall.used.CHAT_MESSAGES.month' as const);
    case 'CALL_MINUTES':
      return window === 'lifetime' ? ('paywall.trialCall' as const) : ('paywall.used.CALL_MINUTES.month' as const);
    case 'WORD_GAMES':
      return 'paywall.used.WORD_GAMES.day' as const;
  }
}

/**
 * Пункты карточки тарифа по порядку. `common: false` — только то, чем
 * тарифы различаются, без открытого на всех («Снежный ком», справочник,
 * карман): так компактнее карточка в модалке лимита.
 */
export function planFeatures(t: TFunction, plan: Plan, { common = true }: { readonly common?: boolean } = {}): readonly string[] {
  const rules = PLAN_RULES[plan];
  const items: string[] = [rules.lessonsPerLevel === null ? t('plans.feature.allLessons') : t('plans.feature.firstLessons', { count: rules.lessonsPerLevel })];
  for (const metric of METRIC_ORDER) {
    const limit = rules.limits[metric];
    if (metric === 'LESSONS') {
      if (limit !== undefined) items.push(limitText(t, metric, limit));
      continue;
    }
    items.push(limit === undefined || limit.fairUse === true ? t(`plans.unlimited.${metric}`) : limitText(t, metric, limit));
  }
  // Игры и инструменты без лимита — на всех тарифах.
  if (common) items.push(t('plans.feature.snowball'), t('plans.feature.guide'), t('plans.feature.pocket'));
  return items;
}
